import React, { createContext, useCallback, useContext } from 'react';
import { useMainProviderContext } from 'context/main/provider';
import { useCreateInteractiveCtaClickMutation } from 'queries/stats/createInteractiveCtaClickMutation';
import { InteractiveCTAOption } from 'types';
import { onInteractiveCTAClick } from 'utils/helpers';

type TrackInteractiveCtaClick = (ctaItem: InteractiveCTAOption) => void;

// no-op by default, so players rendered outside a provider (builder preview,
// video reply) never record clicks
const InteractiveCtaTrackingContext = createContext<TrackInteractiveCtaClick>(
  () => {}
);

type Props = {
  children?: React.ReactNode;
  shortUrl?: string;
  viewerStatisticId?: number;
  designId: number;
  isPreview: boolean;
};

export const InteractiveCtaTrackingProvider = ({
  children,
  shortUrl,
  viewerStatisticId,
  designId,
  isPreview,
}: Props) => {
  const { mutate: createInteractiveCtaClick } =
    useCreateInteractiveCtaClickMutation();

  const trackInteractiveCtaClick = useCallback(
    (ctaItem: InteractiveCTAOption) => {
      // CTAs saved before ids existed have no id until the backfill runs
      if (isPreview || !shortUrl || !ctaItem.id) {
        return;
      }
      createInteractiveCtaClick({
        data: {
          interactiveCtaId: ctaItem.id,
          viewerStatisticId: viewerStatisticId || -1,
          designId,
        },
        shortUrl,
      });
    },
    [
      isPreview,
      shortUrl,
      viewerStatisticId,
      designId,
      createInteractiveCtaClick,
    ]
  );

  return (
    <InteractiveCtaTrackingContext.Provider value={trackInteractiveCtaClick}>
      {children}
    </InteractiveCtaTrackingContext.Provider>
  );
};

// landing page variant, same viewer/design rules as CtaSetsButtons
export const MainInteractiveCtaTrackingProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const { response, shortUrl, isPreview, triggerViewNotification } =
    useMainProviderContext();

  return (
    <InteractiveCtaTrackingProvider
      shortUrl={shortUrl}
      viewerStatisticId={
        triggerViewNotification ? response?.viewerStatisticId : 0
      }
      designId={
        response?.landingPageDesign ? response.landingPageDesign.id : -2
      }
      isPreview={isPreview}
    >
      {children}
    </InteractiveCtaTrackingProvider>
  );
};

// tracks the click, then opens the CTA the same way as before
export const useInteractiveCtaClick = () => {
  const trackInteractiveCtaClick = useContext(InteractiveCtaTrackingContext);
  return useCallback(
    (ctaItem: InteractiveCTAOption, setShowCtaVideoModal: Function) => {
      trackInteractiveCtaClick(ctaItem);
      onInteractiveCTAClick(ctaItem, setShowCtaVideoModal);
    },
    [trackInteractiveCtaClick]
  );
};
